import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { equipmentApi } from '@/api/equipmentApi'
import { useEquipmentScope } from '@/components/equipment/equipmentScope'
import { PENDING_ACTIONS_KEY } from '@/hooks/usePendingActions'
import { FACILITY_OVERVIEW_KEY } from '@/hooks/useFacility'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { procedureRunPath } from '@/lib/procedureRunPath'
import { useTenant } from '@/providers/TenantProvider'
import type { MaintenanceTask } from '@/types/maintenanceTask'

/**
 * Start a task that carries a form (z8nrz7fgzw): straight into its run, with
 * no dialog. The forms carry the signatures, and completing them ends the task.
 *
 * `start` resolves to `{ holdsStock }` when Start needs the Start dialog after
 * all — a unit still holding stock starts only with an override reason, which
 * only the dialog asks for — carrying the server's account of what the unit
 * holds; `open` then takes the started task to its run.
 */
export type TaskFormOutcome = 'opened' | 'failed' | { holdsStock: string }

export function useTaskForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedTenantId } = useTenant()
  const { variant } = useEquipmentScope()
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState<string | null>(null)

  const go = async (task: MaintenanceTask, begin: boolean): Promise<TaskFormOutcome> => {
    if (!selectedTenantId || starting) return 'failed'
    setError(null)
    setStarting(task.id)
    try {
      const { procedureId } = begin
        ? await equipmentApi.startTaskProcedure(selectedTenantId, task.id)
        : await equipmentApi.openTaskProcedure(selectedTenantId, task.id)
      void queryClient.invalidateQueries({ queryKey: ['maintenance-events'] })
      void queryClient.invalidateQueries({ queryKey: [PENDING_ACTIONS_KEY] })
      void queryClient.invalidateQueries({ queryKey: [FACILITY_OVERVIEW_KEY] })
      // A storage unit's task opens under Inventory, whose Tasks tab started it
      // (z8nrz7fjzc); its template is an Equipment one, so the type cannot say.
      const path =
        variant === 'storage'
          ? `/inventory/runs/${encodeURIComponent(procedureId)}`
          : procedureRunPath(variant === 'room' ? 'Cleaning' : 'Equipment', procedureId, null)
      if (path) navigate(path)
      return 'opened'
    } catch (err) {
      if (begin && (err as { status?: number }).status === 409) {
        return { holdsStock: apiErrorMessage(err, 'This unit still holds stock.') }
      }
      setError(apiErrorMessage(err, "Could not open this task's form."))
      return 'failed'
    } finally {
      setStarting(null)
    }
  }

  return {
    start: (task: MaintenanceTask) => go(task, true),
    open: (task: MaintenanceTask) => go(task, false),
    error,
    starting,
  }
}
